import type { YMapControls, YMapControl, YMapGroupEntity, YMap, YMapCollection, YMapFeatureProps, LngLat, YMapFeature, YMapMarker, YMapMarkerProps } from '@yandex/ymaps3-types'
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'
import type { Ref } from 'vue'

import type {
    DispatcherWorkplaceInitializeHelperModuleOption,
    DispatcherWorkplaceDestroyHelperModuleOption,
    ClusterFeatureEntity,
    DispatcherWorkplaceTake,
    DispatcherWorkplaceDelivery,
    DispatcherWorkplaceCourier,
    DispatcherWorkplaceRenderingEntity,
    DispatcherWorkplaceCourierLocation,
    FeaturePopupsOpenPopupOptions,
    DispatcherWorkplaceDataEntity,
    DispatcherWorkplaceItem
} from '@/modules/DispatcherWorkplaceModule/types'

import { reactive, computed } from 'vue'

import DwDoneIcon from '@/modules/DispatcherWorkplaceModule/components/DwDoneIcon/index.vue'
import DwProblemsIcon from '@/modules/DispatcherWorkplaceModule/components/DwProblemsIcon/index.vue'
import DwSidebarEntity from '@/modules/DispatcherWorkplaceModule/components/DwSidebar/Entity.vue'

import { AVAILABLE_COLORS } from '@/modules/DispatcherWorkplaceModule/constants'

import mapControls from '@/modules/YandexMap3Module/map-controls'
import mapObjects from '@/modules/YandexMap3Module/map-objects'
import mapCluster from '@/modules/YandexMap3Module/map-cluster'

import dwMapHelpers from '@/modules/DispatcherWorkplaceModule/dw-map-helpers'

import { useDebounce } from 'vue-composable'

const GRID_SIZE = 100
const DEBOUNCE_TIME = 1000

type QuickFiltersCallbacks = {
    onUndeliveredDeliveriesChanged: (is_enabled: boolean) => void
    onUnassignedTakesChanged: (is_enabled: boolean) => void
}

export default function({ openPopup, selectedFeatureId }: {
    openPopup: (options: FeaturePopupsOpenPopupOptions) => void
    selectedFeatureId: Ref<number | null>
}) {
    let mapInstance: YMap | null = null
    let ordersClusterer: any | null = null
    let couriersRoutesCollections: YMapCollection | null = null
    let couriersActionsCollections: YMapCollection | null = null

    let couriersControlsGroup: YMapControls | null = null
    let couriersControlsControl: YMapControl | null = null
    let couriersControlsHorizontalGroup: YMapGroupEntity<{}> | null = null

    let quickFiltersCallbacks: QuickFiltersCallbacks | null = null

    const state = reactive({
        list: [] as DispatcherWorkplaceCourier[],
        showed_routes_couriers_ids: [] as number[],
        quick_filters: {
            completed_orders: false,
            deliveries: true,
            takes: true,
            problems: false,
            undelivered_deliveries: false,
            unassigned_takes: false,
            order_or_invoice_number: null as string | null,
            finded_orders_or_invoices: [] as DispatcherWorkplaceDataEntity[]
        }
    })

    const getCouriersWithAvailableColors = (couriers: Array<DispatcherWorkplaceCourier>): Array<DispatcherWorkplaceCourier> => {
        return couriers.map((courier, index) => ({
            ...courier,
            localColor: AVAILABLE_COLORS[index % AVAILABLE_COLORS.length]
        }))
    }

    const updateList = (couriers: Array<DispatcherWorkplaceCourier>) => {
        state.list = getCouriersWithAvailableColors(couriers)
    }

    const onFindedEntityClicked = (entity: DispatcherWorkplaceDataEntity) => {
        state.quick_filters.order_or_invoice_number = null
        state.quick_filters.finded_orders_or_invoices = []

        onCourierEntityClicked(entity)
    }

    const searchOrder = () => {
        if (!state.quick_filters.order_or_invoice_number) {
            state.quick_filters.finded_orders_or_invoices = []
            return
        }
        
        let results: DispatcherWorkplaceDataEntity[] = []
        const query = (state.quick_filters.order_or_invoice_number as string).toLowerCase()

        state.list.forEach((courier) => {
            results = results.concat(courier.items.filter((entity) => {
                if (entity.type === 1) {
                    const entityOrderNumber = (entity as DispatcherWorkplaceTake).orderNumber.toLowerCase()
                    return entityOrderNumber.includes(query)
                }
    
                if (entity.type === 2) {
                    const entityInvoiceNumber = (entity as DispatcherWorkplaceDelivery).invoiceNumber.toLowerCase()
                    return entityInvoiceNumber.includes(query)
                }
            }))
        })

        state.quick_filters.finded_orders_or_invoices = results
    }

    const debouncedSearchOrder = useDebounce(searchOrder, DEBOUNCE_TIME)

    const initializeMapControls = () => {
        if (!couriersControlsGroup) return

        const { YMapControl } = ymaps3

        couriersControlsControl = new YMapControl()
        couriersControlsGroup.addChild(couriersControlsControl)

        couriersControlsHorizontalGroup = mapControls.createHorizontalControlsGroup()
        couriersControlsControl.addChild(couriersControlsHorizontalGroup)

        couriersControlsHorizontalGroup.addChild(
            mapControls.createIconButtonControl({
                checked: computed(() => state.quick_filters.completed_orders),
                icon: DwDoneIcon,
                onChange: () => {
                    state.quick_filters.completed_orders = !state.quick_filters.completed_orders

                    if (state.quick_filters.completed_orders) {
                        state.quick_filters.unassigned_takes = false
                        state.quick_filters.undelivered_deliveries = false

                        if (quickFiltersCallbacks && quickFiltersCallbacks.onUndeliveredDeliveriesChanged) {
                            quickFiltersCallbacks.onUndeliveredDeliveriesChanged(state.quick_filters.undelivered_deliveries)
                        }

                        if (quickFiltersCallbacks && quickFiltersCallbacks.onUnassignedTakesChanged) {
                            quickFiltersCallbacks.onUnassignedTakesChanged(state.quick_filters.unassigned_takes)
                        }

                        if (state.quick_filters.takes === false && state.quick_filters.deliveries === false) {
                            state.quick_filters.takes = true
                            state.quick_filters.deliveries = true
                        }
                    }

                    updateCluster()
                }
            })
        )


        couriersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.deliveries),
                label: 'Доставки',
                onChange: () => {
                    state.quick_filters.deliveries = !state.quick_filters.deliveries
                    updateCluster()
                }
            })
        )

        couriersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.takes),
                label: 'Заборы',
                onChange: () => {
                    state.quick_filters.takes = !state.quick_filters.takes
                    updateCluster()
                }
            })
        )

        couriersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.undelivered_deliveries),
                label: 'Невыданные доставки',
                onChange: () => {
                    state.quick_filters.undelivered_deliveries = !state.quick_filters.undelivered_deliveries

                    if (state.quick_filters.undelivered_deliveries) {
                        state.quick_filters.completed_orders = false
                        updateCluster()
                    }
                    
                    if (quickFiltersCallbacks && quickFiltersCallbacks.onUndeliveredDeliveriesChanged) {
                        quickFiltersCallbacks.onUndeliveredDeliveriesChanged(state.quick_filters.undelivered_deliveries)
                    }
                }
            })
        )

        couriersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.unassigned_takes),
                label: 'Неназначенные заборы',
                onChange: () => {
                    state.quick_filters.unassigned_takes = !state.quick_filters.unassigned_takes

                    if (state.quick_filters.unassigned_takes) {
                        state.quick_filters.completed_orders = false
                        updateCluster()
                    }
                    
                    if (quickFiltersCallbacks && quickFiltersCallbacks.onUnassignedTakesChanged) {
                        quickFiltersCallbacks.onUnassignedTakesChanged(state.quick_filters.unassigned_takes)
                    }
                }
            })
        )

        couriersControlsHorizontalGroup.addChild(
            mapControls.createSearchControl({
                value: computed(() => state.quick_filters.order_or_invoice_number),
                placeholder: 'Поиск по номеру заказа или накладной',
                options: computed(() => state.quick_filters.finded_orders_or_invoices),
                option: DwSidebarEntity,
                onInput: (event: Event) => {
                    const target = event.target as HTMLInputElement
                    state.quick_filters.order_or_invoice_number = target.value
                    debouncedSearchOrder()
                },
                onOptionClicked: onFindedEntityClicked
            })
        )
    }

    const destroyMapControls = () => {
        if (!couriersControlsGroup || !couriersControlsControl || !couriersControlsHorizontalGroup) return

        couriersControlsControl.removeChild(couriersControlsHorizontalGroup)
        couriersControlsHorizontalGroup = null

        couriersControlsGroup.removeChild(couriersControlsControl)
        couriersControlsControl = null
    }

    const initializeCouriersRoutesCollection = () => {
        if (!mapInstance) return

        const { YMapCollection } = ymaps3
        couriersRoutesCollections = new YMapCollection({})
        mapInstance.addChild(couriersRoutesCollections)
    }

    const initializeCouriersActionsCollection = () => {
        if (!mapInstance) return

        const { YMapCollection } = ymaps3
        couriersActionsCollections = new YMapCollection({})
        mapInstance.addChild(couriersActionsCollections)
    }
    
    const initializeModule = (options: DispatcherWorkplaceInitializeHelperModuleOption) => {
        mapInstance = options.mapInstance
        couriersControlsGroup = options.controlsGroup

        initializeMapControls()
        initializeCouriersRoutesCollection()
        initializeCouriersActionsCollection()
        initializeMapClusterer()

        renderAllSelectedCouriersLocations()
        renderAllSelectedCouriersActions()
    }

    const isTakeCompleted = (entity: DispatcherWorkplaceItem) => {
        if (entity.type === 1) {
            return entity.state === 1 || entity.statusId === 3
        }

        return false
    }

    const isDeliveryCompleted = (entity: DispatcherWorkplaceItem) => {
        if (entity.type === 2) {
            return entity.state === 1
        }

        return false
    }

    const isFeatureEntityCompleted = (entity: DispatcherWorkplaceItem) => {
        if (entity.type === 1) {
            return isTakeCompleted(entity)
        }

        if (entity.type === 2) {
            return isDeliveryCompleted(entity)
        }

        return false
    }

    const getActualClusterFeatures = () => {
        let ordersToRender: DispatcherWorkplaceRenderingEntity[] = []

        state.showed_routes_couriers_ids.forEach((courier_id) => {
            const courier = state.list.find((courier) => courier.courierId === courier_id)

            if (courier) {
                const completedTakes = courier.items.filter(isTakeCompleted)
                const completedDeliveries = courier.items.filter(isDeliveryCompleted)

                const takes = courier.items.filter((order) => order.type === 1 && !((order as DispatcherWorkplaceTake).notAssigned) && !isFeatureEntityCompleted(order))
                const deliveries = courier.items.filter((order) => order.type === 2 && !((order as DispatcherWorkplaceDelivery).notDelivered) && !isFeatureEntityCompleted(order))

                const unassigned = courier.items.filter((order) => order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned)
                const undelivered = courier.items.filter((order) => order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered)

                ordersToRender = ordersToRender.concat(
                    courier.items.filter((order) => {
                        const isCompletedTake = isTakeCompleted(order)
                        const isCompletedDelivery = isDeliveryCompleted(order)

                        const isTake = order.type === 1 && !(order as DispatcherWorkplaceTake).notAssigned && !isFeatureEntityCompleted(order)
                        const isDelivery = order.type === 2 && !(order as DispatcherWorkplaceDelivery).notDelivered && !isFeatureEntityCompleted(order)
                        const isUnassignedTake = order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned
                        const isUndeliveredDelivery = order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered

                        return !isCompletedTake && !isCompletedDelivery && !isTake && !isDelivery && !isUnassignedTake && !isUndeliveredDelivery
                    })
                )

                if (state.quick_filters.completed_orders) {
                    if (state.quick_filters.takes) {
                        ordersToRender = ordersToRender.concat(completedTakes)
                    }

                    if (state.quick_filters.deliveries) {
                        ordersToRender = ordersToRender.concat(completedDeliveries)
                    }
                } else {
                    if (state.quick_filters.takes) {
                        ordersToRender = ordersToRender.concat(takes)
                    }

                    if (state.quick_filters.deliveries) {
                        ordersToRender = ordersToRender.concat(deliveries)
                    }
                }
        
                if (state.quick_filters.unassigned_takes) {
                    ordersToRender = ordersToRender.concat(unassigned)
                }
        
                if (state.quick_filters.undelivered_deliveries) {
                    ordersToRender = ordersToRender.concat(undelivered)
                }
            }
        })

        return ordersToRender
    }

    const updateCluster = () => {
        if (!ordersClusterer) return

        const features = getActualClusterFeatures().map(dwMapHelpers.convertOrderOrLocationToClusterFeature)
        ordersClusterer.update({ features })
    }

    const onFeatureChangeDate = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: true,
            is_add_to_wait_list: false,
            is_assign: false,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureAddToWaitList = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: true,
            is_assign: false,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureAssign = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: false,
            is_assign: true,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureSelfDelivery = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: false,
            is_assign: false,
            is_self_delivery: true,
            feature
        })
    }

    const initializeMapClusterer = async () => {
        if (!mapInstance) return

        ordersClusterer = await mapCluster.createCluster<ClusterFeatureEntity>({
            gridSize: GRID_SIZE,
            features: getActualClusterFeatures().map(dwMapHelpers.convertOrderOrLocationToClusterFeature),
            markerTemplate: (clusterer: any) => dwMapHelpers.convertFeatureToMarkerElement(clusterer, true),
            clusterTemplate: dwMapHelpers.convertFeaturesToClusterElement,
            clusterPopup: (
                coordinates: LngLat,
                features: Array<ClusterFeatureEntity>,
                close: () => void,
            ) => dwMapHelpers.createClusterPopup(
                coordinates,
                features,
                selectedFeatureId.value,
                close,
                {
                    changeDate: onFeatureChangeDate,
                    addToWaitList: onFeatureAddToWaitList,
                    assign: onFeatureAssign,
                    selfDelivery: onFeatureSelfDelivery
                },
                true
            ),
            markerPopup: (
                feature: ClusterFeatureEntity,
                close: () => void
            ) => dwMapHelpers.createMarkerPopup(
                feature,
                close,
                {
                    changeDate: onFeatureChangeDate,
                    addToWaitList: onFeatureAddToWaitList,
                    assign: onFeatureAssign,
                    selfDelivery: onFeatureSelfDelivery
                }
            )
        })

        mapInstance.addChild(ordersClusterer)
    }

    const destroyMapClusterer = () => {
        if (!mapInstance || !ordersClusterer) return

        mapInstance.removeChild(ordersClusterer)
        ordersClusterer = null
    }

    const destroyModule = (options: DispatcherWorkplaceDestroyHelperModuleOption) => {
        destroyMapControls()
        deleteAllCouriersLocations()
        deleteAllCouriersActions()
        destroyMapClusterer()

        if (options.resetStatement) {
            state.showed_routes_couriers_ids = []
            state.quick_filters.completed_orders = false
            state.quick_filters.problems = false
            state.quick_filters.undelivered_deliveries = false
            state.quick_filters.unassigned_takes = false
            state.quick_filters.order_or_invoice_number = null
        }
    }

    const convertCourierLocationsToCoordinates = (locations: Array<DispatcherWorkplaceCourierLocation>): LngLat[] => {
        return locations.filter((location) => Boolean(location.latitude) && Boolean(location.longitude)).map((location) => {
            return [Number(location.longitude as string), Number(location.latitude as string)]
        })
    }

    const convertCourierToFeatureProps = (courier: DispatcherWorkplaceCourier): YMapFeatureProps => {
        return {
            id: `courier-${courier.courierId}-location`,
            geometry: {
                type: 'LineString',
                coordinates: convertCourierLocationsToCoordinates(courier.locations),
            },
            style: {
                stroke: [{
                    width: 5,
                    color: courier.localColor as string,
                    // color: `linear-gradient(${courier.localColor}, #000000)`
                }],
                zIndex: 2
            },
        }
    }

    const renderCourierLocations = (courier: DispatcherWorkplaceCourier) => {
        (couriersRoutesCollections as YMapCollection).addChild(
            mapObjects.createFeature(convertCourierToFeatureProps(courier))
        )
    }

    const deleteCourierLocations = (courier: DispatcherWorkplaceCourier) => {
        const children = (couriersRoutesCollections as YMapCollection).children.find((route) => (route as any).id === `courier-${courier.courierId}-location`)

        if (children) {
            (couriersRoutesCollections as YMapCollection).removeChild(children)
        }
    }

    const deleteAllCouriersLocations = () => {
        if (!mapInstance || !couriersRoutesCollections) return
        mapInstance.removeChild(couriersRoutesCollections)
    }

    const renderAllSelectedCouriersLocations = () => {
        if (state.showed_routes_couriers_ids.length === 0) return

        state.showed_routes_couriers_ids.forEach((courierId) => {
            const findedCourier = state.list.find((courier) => courierId === courier.courierId)
            renderCourierLocations(findedCourier as DispatcherWorkplaceCourier)
        })
    }

    const renderAllSelectedCouriersActions = () => {
        if (state.showed_routes_couriers_ids.length === 0) return

        state.showed_routes_couriers_ids.forEach((courierId) => {
            const findedCourier = state.list.find((courier) => courierId === courier.courierId)
            renderCourierActions(findedCourier as DispatcherWorkplaceCourier)
        })
    }

    const renderCourierActions = (courier: DispatcherWorkplaceCourier) => {
        if (couriersActionsCollections) {
            if (courier.firstAction.length) {
                const startAction = courier.firstAction[0]
                let text = 'Неопределенное действие'

                if (startAction.client.type === 1) {
                    text = 'Доставка'
                } else if (startAction.client.type === 2) {
                    text = 'Забор'
                } else if (startAction.client.type === 3) {
                    text = 'Получение выдачи на складе'
                } else if (startAction.client.type === 4) {
                    text = 'Возврат выдачи'
                }

                const { YMapMarker } = ymaps3
                const marker = new YMapMarker(
                    {
                        coordinates: [Number(startAction.longitude), Number(startAction.latitude)],
                        properties: { hint: { text } },
                        zIndex: 10,
                    },
                    dwMapHelpers.convertCourierToStartIcon(courier)
                )

                couriersActionsCollections.addChild(marker)
            }

            if (courier.locations.length) {
                const lastLocation = courier.locations[courier.locations.length - 1]

                const { YMapMarker } = ymaps3

                const marker = new YMapMarker(
                    {
                        coordinates: [Number(lastLocation.longitude), Number(lastLocation.latitude)],
                        properties: { hint: { text: 'Последнее местоположение' } },
                        zIndex: 11,
                    },
                    dwMapHelpers.convertCourierToLastIcon(courier)
                )

                couriersActionsCollections.addChild(marker)
            }
        }
    }

    const deleteAllCouriersActions = () => {
        if (!mapInstance || !couriersActionsCollections) return
        mapInstance.removeChild(couriersActionsCollections)
    }

    const toggleRoute = (courier: DispatcherWorkplaceCourier) => {
        if (state.showed_routes_couriers_ids.length) {
            state.showed_routes_couriers_ids = []

            deleteAllCouriersLocations()
            deleteAllCouriersActions()

            initializeCouriersRoutesCollection()
            initializeCouriersActionsCollection()
        }

        renderCourierLocations(courier)
        renderCourierActions(courier)

        if (courier.locations.length > 0) {
            dwMapHelpers.updateMapLocation({
                map: mapInstance as YMap,
                coordinates: [Number(courier.locations[0].longitude), Number(courier.locations[1].latitude)],
                zoom: 12
            })
        }

        state.showed_routes_couriers_ids.push(courier.courierId)
        updateCluster()

        // if (state.showed_routes_couriers_ids.includes(courier.courierId)) {
        //     deleteCourierLocations(courier)

        //     const index = state.showed_routes_couriers_ids.findIndex((id) => id === courier.courierId)
        //     state.showed_routes_couriers_ids.splice(index, 1)

        //     updateCluster()
        // } else {
        //     renderCourierLocations(courier)

        //     if (courier.locations.length > 0) {
        //         dwMapHelpers.updateMapLocation({
        //             map: mapInstance as YMap,
        //             coordinates: [Number(courier.locations[0].longitude), Number(courier.locations[1].latitude)],
        //             zoom: 12
        //         })
        //     }

        //     state.showed_routes_couriers_ids.push(courier.courierId)
        //     updateCluster()
        // }

        destroyMapControls()
        initializeMapControls()
    }

    const openClusterPopupOnMap = (dataEntity: DispatcherWorkplaceDataEntity) => {
        if (ordersClusterer) {
            if (dataEntity.type === 1) {
                ordersClusterer.openPopup(`order-${(dataEntity as DispatcherWorkplaceTake).orderNumber}`)
            }

            if (dataEntity.type === 2) {
                ordersClusterer.openPopup(`invoice-${(dataEntity as DispatcherWorkplaceDelivery).invoiceNumber}`)
            }
        }
    }

    const onCourierEntityClicked = (dataEntity: DispatcherWorkplaceDataEntity) => {
        dwMapHelpers.updateMapLocation({
            map: mapInstance as YMap,
            coordinates: [Number(dataEntity.longitude as string), Number(dataEntity.latitude as string)],
            zoom: 17
        })

        if (dataEntity.type === 1) {
            if ((dataEntity as DispatcherWorkplaceTake).notAssigned && !state.quick_filters.unassigned_takes) {
                state.quick_filters.unassigned_takes = true
                updateCluster()
            }
        }

        if (dataEntity.type === 2) {
            if ((dataEntity as DispatcherWorkplaceDelivery).notDelivered && !state.quick_filters.undelivered_deliveries) {
                state.quick_filters.undelivered_deliveries = true
                updateCluster()
            }
        }

        openClusterPopupOnMap(dataEntity)

        if (!dataEntity.courierId || state.showed_routes_couriers_ids.includes(dataEntity.courierId)) return

        const courier = state.list.find((courier) => courier.courierId === dataEntity.courierId)
        if (!courier) return

        toggleRoute(courier)
    }

    const setQuickFiltersCallbacks = (ordersQuickFiltersCallbacks: QuickFiltersCallbacks) => {
        if (ordersQuickFiltersCallbacks) {
            quickFiltersCallbacks = {
                onUndeliveredDeliveriesChanged: ordersQuickFiltersCallbacks.onUndeliveredDeliveriesChanged,
                onUnassignedTakesChanged: ordersQuickFiltersCallbacks.onUnassignedTakesChanged
            }
        }
    }

    const setUndeliveredDeliveriesQuckFilters = (is_enabled: boolean) => {
        state.quick_filters.undelivered_deliveries = is_enabled

        if (is_enabled) {
            state.quick_filters.completed_orders = false
            updateCluster()
        }
    }

    const setUnassignedTakesQuckFilters = (is_enabled: boolean) => {
        state.quick_filters.unassigned_takes = is_enabled

        if (is_enabled) {
            state.quick_filters.completed_orders = false
            updateCluster()
        }
    }

    return {
        state,
        updateList,
        initializeModule,
        destroyModule,
        toggleRoute,
        onCourierEntityClicked,
        setUndeliveredDeliveriesQuckFilters,
        setUnassignedTakesQuckFilters,
        setQuickFiltersCallbacks,
    }
}